import { formatISTDate } from "../../lib/admin-time";
import React, { useContext, useEffect, useState } from 'react';
import { APIContext } from '../../api';
import { 
  Button, 
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  ModalCloseButton, 
  useDisclosure, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Td, 
  Badge, 
  IconButton, 
  Tooltip,
  Select,
  Text,
  Box
} from '@chakra-ui/react';
import { PulseLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

function UserAddons({ userSub }) {
  const { getUserAddons, getAvailableAddons, assignAddonToUser, unassignAddonFromUser } = useContext(APIContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);
  const [userAddons, setUserAddons] = useState([]);
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedAddon, setSelectedAddon] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);

  useEffect(() => {
    if (userSub) {
      loadUserAddons();
      loadAvailableAddons();
    }
  }, [userSub]);

  const loadUserAddons = async () => {
    try {
      setIsLoading(true);
      const addons = await getUserAddons(userSub);
      setUserAddons(addons);
    } catch (error) {
      console.error('Error loading user addons:', error);
      toast.error('Failed to load user addons');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableAddons = async () => {
    try {
      const addons = await getAvailableAddons();
      setAvailableAddons(addons);
    } catch (error) {
      console.error('Error loading available addons:', error);
      toast.error('Failed to load available addons');
    }
  };

  const handleAssignAddon = async () => {
    if (!selectedAddon) {
      toast.error('Please select an addon to assign');
      return;
    }

    try {
      setIsAssigning(true);
      await assignAddonToUser(userSub, selectedAddon);
      toast.success('Addon assigned successfully!');
      onClose();
      setSelectedAddon('');
      loadUserAddons();
      loadAvailableAddons();
    } catch (error) {
      console.error('Error assigning addon:', error);
      toast.error('Failed to assign addon');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassignAddon = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to unassign this addon?')) {
      return;
    }

    try {
      setIsUnassigning(true);
      await unassignAddonFromUser(userSub, assignmentId);
      toast.success('Addon unassigned successfully!');
      loadUserAddons();
      loadAvailableAddons();
    } catch (error) {
      console.error('Error unassigning addon:', error);
      toast.error('Failed to unassign addon');
    } finally {
      setIsUnassigning(false);
    }
  };

  const formatDate = (timestamp) => {
    return formatISTDate(timestamp);
  };

  const getFilteredAvailableAddons = () => {
    // Since addons can be stacked, show all available addons
    return availableAddons;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4">
        <PulseLoader size={12}></PulseLoader>
      </div>
    );
  }

  return (
    <div className="w-full border rounded-xl flex flex-col gap-4 p-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Plan Addons</h2>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={onOpen}
          leftIcon={<FontAwesomeIcon icon={faPlus} />}
          isDisabled={getFilteredAvailableAddons().length === 0}
        >
          Assign Addon
        </Button>
      </div>

      {userAddons.length === 0 ? (
        <div className="text-center text-gray-500 py-4">
          <p>No addons assigned to this user.</p>
          {getFilteredAvailableAddons().length > 0 && (
            <p className="text-sm mt-2">Click "Assign Addon" to assign available addons.</p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Addon Name</Th>
                <Th>Price</Th>
                <Th>Limits</Th>
                <Th>Features</Th>
                <Th>Assigned</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {userAddons.map((addon) => (
                <Tr key={addon.assignment_id}>
                  <Td>
                    <div>
                      <div className="font-medium">{addon.name}</div>
                      {addon.description && (
                        <div className="text-sm text-gray-500">{addon.description}</div>
                      )}
                    </div>
                  </Td>
                  <Td>${addon.price || 0}</Td>
                  <Td>
                    <div className="text-sm space-y-1">
                      <div>Websites: {addon.websites_limit || 0}</div>
                      <div>AI Credits: {addon.ai_credits_limit || 0}</div>
                      <div>Storage: {addon.storage_limit || 0}GB</div>
                    </div>
                  </Td>
                  <Td>
                    <div className="space-y-1">
                      {addon.whitelabel && <Badge colorScheme="green" size="sm">Whitelabel</Badge>}
                      {addon.byok && <Badge colorScheme="blue" size="sm">BYOK</Badge>}
                      {addon.webhooks && <Badge colorScheme="purple" size="sm">Webhooks</Badge>}
                    </div>
                  </Td>
                  <Td>{formatDate(addon.assigned_at)}</Td>
                  <Td>
                    <div className="flex space-x-2">
                      <Tooltip label="Unassign">
                        <IconButton
                          size="sm"
                          icon={<FontAwesomeIcon icon={faTrash} />}
                          onClick={() => handleUnassignAddon(addon.assignment_id)}
                          colorScheme="red"
                          variant="ghost"
                          isLoading={isUnassigning}
                        />
                      </Tooltip>
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Addon to User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <div className="space-y-4">
              <div>
                <Text mb={2} fontWeight="medium">Select Addon</Text>
                <Select
                  value={selectedAddon}
                  onChange={(e) => setSelectedAddon(e.target.value)}
                  placeholder="Choose an addon to assign"
                >
                  {getFilteredAvailableAddons().map((addon) => (
                    <option 
                      key={addon.pk} 
                      value={addon.pk.replace('plan-addon-', '')}
                    >
                      {addon.name} - ${addon.price || 0}
                    </option>
                  ))}
                </Select>
              </div>

              {selectedAddon && (
                <Box p={4} bg="gray.50" borderRadius="md">
                  <Text fontWeight="medium" mb={2}>Addon Details:</Text>
                  {(() => {
                    const addon = availableAddons.find(a => a.pk === `plan-addon-${selectedAddon}`);
                    if (!addon) return null;
                    
                    return (
                      <div className="text-sm space-y-1">
                        <div><strong>Name:</strong> {addon.name}</div>
                        <div><strong>Description:</strong> {addon.description || 'No description'}</div>
                        <div><strong>Price:</strong> ${addon.price || 0}</div>
                        <div><strong>Limits:</strong></div>
                        <div className="ml-4">• Websites: {addon.websites_limit || 0}</div>
                        <div className="ml-4">• AI Credits: {addon.ai_credits_limit || 0}</div>
                        <div className="ml-4">• Storage: {addon.storage_limit || 0}GB</div>
                        <div><strong>Features:</strong></div>
                        <div className="ml-4">
                          {addon.whitelabel && <Badge colorScheme="green" size="sm" mr={1}>Whitelabel</Badge>}
                          {addon.byok && <Badge colorScheme="blue" size="sm" mr={1}>BYOK</Badge>}
                          {addon.webhooks && <Badge colorScheme="purple" size="sm">Webhooks</Badge>}
                        </div>
                      </div>
                    );
                  })()}
                </Box>
              )}
            </div>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleAssignAddon}
              isLoading={isAssigning}
              loadingText="Assigning..."
              isDisabled={!selectedAddon}
            >
              Assign Addon
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}

export default UserAddons; 